import {
  LitElement,
  html,
  css,
  ifDefined,
  classMap,
  styles,
} from '@hh/global.js'

class HHCustomDropdown extends LitElement {
  static properties = {
    options: {
      type: Object,
    },
    name: {
      type: String,
    },
    active: {
      type: String,
    },
    lang: {
      type: Boolean,
    },
    position: {
      type: String,
    },
    _selected: {
      state: true,
    },
    _active: {
      state: true,
    },
    open: {
      type: Boolean,
      reflect: true,
    },
    disabled: {
      type: Boolean,
      reflect: true,
    },
    defaultUnselected: {
      type: Boolean,
      reflect: true,
      attribute: 'default-unselected',
    },
    selected: {
      type: Boolean,
      reflect: true,
    },
    _currentOption: {
      state: true,
    },
  }

  constructor() {
    super()
    this.handleBodyClick = this.handleBodyClick.bind(this)
    this.uuid = Math.random() * (1000000 - 1) + 1
    this.position = 'bottom'
    this._active = false
  }

  connectedCallback() {
    super.connectedCallback()
    this._selected = this.active
    document.body.addEventListener('click', this.handleBodyClick)
    document.addEventListener('focusin', this.handleDocumentFocusIn.bind(this))
    this.addEventListener('keydown', this.handleKeydown.bind(this))
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    document.body.addEventListener('click', this.handleBodyClick)
    document.removeEventListener('focusin', this.handleFocusIn)
    this.removeEventListener('keydown', this.handleKeydown)
  }

  handleBodyClick(e) {
    const path = e.composedPath()
    if (
      path.some((node) => {
        return node === this
      })
    ) {
      return true
    }

    this._active && this.toggle()
  }

  toggle(e = false) {
    if (this.disabled) return
    if (e && e.type === 'keyup' && !~['ESCAPE'].indexOf(e.code.toUpperCase())) {
      return true
    }

    this._active = !this._active
    this.open = this._active
  }

  selectValue(e, value) {
    e.preventDefault()
    this._selected = value
    ;(this.querySelector('[slot="input"]') || {}).value = value
    this.dispatchEvent(
      new CustomEvent('change', {
        bubbles: true,
        composed: true,
        detail: {
          value,
        },
      })
    )
  }

  get selectedLabel() {
    const option = this.options.find(
      (option) => option.value === this._selected
    )

    if (!option && this.defaultUnselected) return ''

    if (!option) return 'Invalid selected option'
    this.selected = true
    return option.label
  }

  handleKeydown(e) {
    if (e.key.toUpperCase() === 'ESCAPE') {
      e.preventDefault()
      e.stopPropagation()
      this._active = false
      this.open = false
      this.button.focus({ preventScroll: true })
    }

    if (e.code.toUpperCase() == 'SPACE' || e.key === ' ') {
      if (this._active) {
        e.preventDefault()
        e.stopPropagation()
      }

      this.selectValue(e, this._currentOption.dataset.optionValue)
      this.button.focus({ preventScroll: true })
      return
    }

    if (['ArrowUp', 'ArrowDown'].includes(e.key)) {
      const allOptions = this._getAllOptions()
      const currentIndex = allOptions.indexOf(this._currentOption)
      let newIndex = Math.max(0, currentIndex)

      // Prevent scrolling
      e.preventDefault()

      if (!this._active) {
        this.toggle(e)
        if (this._currentOption) {
          this._currentOption.focus({ preventScroll: true })
          return
        }
      }

      if (e.key === 'ArrowDown') {
        newIndex = currentIndex + 1
      } else if (e.key === 'ArrowUp') {
        newIndex = currentIndex - 1
      }

      this._setCurrentOption(allOptions[newIndex])
    }
  }

  _getAllOptions() {
    return [...this.renderRoot?.querySelectorAll('.link')]
  }

  _getFirstOption() {
    return this.renderRoot?.querySelector('.link')
  }

  _setCurrentOption(option) {
    const allOptions = this._getAllOptions()

    // Clear selection
    allOptions.forEach((el) => {
      el.tabIndex = -1
    })

    // Select the target option
    if (option) {
      this._currentOption = option
      option.tabIndex = 0
      setTimeout(() => {
        option.focus()
      }, 100)
    }
  }

  handleDocumentFocusIn(e) {
    // Close when focusing out of the select
    const path = e.composedPath()
    if (this && !path.includes(this)) {
      this._active = false
      this.open = false
    }
  }

  onCloseClick() {
    this._active = false
    this.open = false
  }

  handleEnterKey(e) {
    if (e.key !== 'Enter') return
    this.button.focus({ preventScroll: true })
  }

  sanitize(value) {
    return value.replace('&amp;', '&')
  }

  get button() {
    return this.renderRoot?.querySelector('button') ?? null
  }

  render() {
    const allOptions = this._getAllOptions()
    const currentIndex = allOptions.indexOf(this._currentOption)

    return html`
      <div
        class=${classMap({
          overlay: true,
          open: this._active,
        })}
        @click=${this.onCloseClick}
        aria-hidden="true"
      ></div>
      <div
        class=${classMap({
          container: true,
          open: this._active,
        })}
      >
        <button
          class="button"
          aria-expanded="${this._active}"
          aria-controls="${this.uuid}-list"
          aria-describedby="${this.uuid}-label"
          @click=${this.toggle}
        >
          <slot name="title"></slot>
          <slot name="icon"></slot>
        </button>

        <slot name="input"></slot>
        <ul
          id="${this.uuid}-list"
          class="${classMap({
            list: true,
            'is-active': this._active,
            'position-top': this.position === 'top',
          })}"
          @keyup="${this.toggle}"
          aria-hidden="${!this._active}"
          role="listbox"
          aria-activedescendant="${this.uuid}-list-${currentIndex}"
        >
          ${this.options.map((option, idx) => {
            this.lang && (option.lang = option.value)
            return html`<li
              class="${classMap({
                option: true,
                'is-active': option.value === this._selected,
              })}"
              role="option"
              aria-selected="${option.value === this._selected}"
              id="${this.uuid}-list-${idx}"
            >
              <a
                @click="${(e) => this.selectValue(e, option.value)}"
                @keyup="${this.handleEnterKey}"
                data-option-value="${option.value}"
                class="link"
                href="#"
                hreflang="${ifDefined(option.lang)}"
                lang="${ifDefined(option.lang)}"
              >
                <span class="link-radio" aria-hidden="true"></span>
                <span class="link-text">${this.sanitize(option.label)}</span>
              </a>
            </li>`
          })}
        </ul>
      </div>
    `
  }

  static styles = [
    styles.visuallyHidden,
    css`
      :host *,
      :host *::before,
      :host *::after {
        box-sizing: border-box;
      }

      :host {
        width: 100%;
      }

      .container {
        position: relative;
      }

      .container.open {
        z-index: 99;
      }

      .overlay {
        position: fixed;
        inset: 0;
        z-index: 98;
        background-color: var(--hh-overlay-color, rgba(0, 0, 0, 0.5));
        backdrop-filter: blur(var(--hh-overlay-blur, 2.5px));
        -webkit-backdrop-filter: blur(var(--hh-overlay-blur, 2.5px));
        opacity: 0;
        pointer-events: none;
        transition: opacity var(--hh-overlay-duration, --hh-duration-default)
          ease-in-out;
        min-width: 100%;
        min-height: 100%;
        width: 100vw;
        height: 100vh;
      }

      .overlay.open {
        opacity: 1;
        pointer-events: auto;
      }

      :host([disabled]) .container {
        background-color: var(--hh-color-input-disabled, #f5f5f5);
      }

      .label-wrapper {
        cursor: pointer;
      }

      .button {
        align-items: center;
        background: var(--hh-color-day);
        border: 0.5rem solid var(--hh-color-day);
        border-radius: 3rem;
        color: var(--hh-color-text);
        box-shadow: 0 1px 15px rgba(0, 0, 0, 0.05);
        padding: var(--hh-spacing-xxs) var(--hh-spacing-sm);
        cursor: pointer;
        display: flex;
        justify-content: space-between;
        height: (var(--hh-height), 3.6rem);
        width: 100%;
      }

      .button:focus-visible {
        outline: 0.2rem solid var(--hh-color-focus);
        outline-offset: var(--hh-focus-outline-offset);
      }

      :host([disabled]) .button {
        color: var(--hh-color-subdued);
        border: none;
      }

      ::slotted([slot='button']),
      ::slotted([slot='icon']) {
        align-items: center;
        display: flex;
        height: 100%;
        color: currentColor;
      }

      ::slotted([slot='icon']) {
        width: 1.6rem;
        height: 1.6rem;
        margin-left: var(--hh-spacing-xxs);
        transition: transform var(--hh-duration-default, 300ms);
      }

      :host([open]) ::slotted([slot='icon']) {
        transform: rotate(180deg);
      }

      ::slotted([slot='title']) {
        font: var(--hh-button-medium-font);
        letter-spacing: var(--hh-button-letter-spacing);
        text-transform: uppercase;
        margin: 0;
      }

      :host([default-unselected]) ::slotted([slot='title']) {
        font: var(--hh-body-font);
        color: var(--hh-color-text);
        inset: 2rem 0px 0px;
        transform: translateY(0px);
        transition: transform var(--hh-duration-medium, 300ms),
          font-size var(--hh-duration-medium, 300ms);
      }

      :host([open][default-unselected]) ::slotted([slot='title']) {
        color: var(--hh-color-subdued, #ccc);
      }

      :host([disabled]) ::slotted([slot='title']) {
        color: var(--hh-color-subdued);
      }

      .list {
        background-color: var(--hh-background, white);
        border-radius: var(--hh-border-radius, 0.8rem);
        display: block;
        margin: var(--hh-list-margin, 0.8rem 0 0);
        opacity: 0;
        overflow-y: auto;
        padding: var(--hh-spacing-md);
        position: absolute;
        transition: visibility var(--hh-duration-default, 300ms)
            var(--hh-duration-default, 300ms),
          opacity var(--hh-duration-default, 300ms),
          transform var(--hh-duration-default, 300ms);
        visibility: hidden;
        width: var(--hh-list-width, 100%);
        right: 0;
        z-index: 1;
      }

      .list.position-top {
        bottom: 100%;
      }

      .list.is-active {
        opacity: 1;
        transition: transform var(--hh-duration-default, 300ms) 1ms,
          opacity var(--hh-duration-default, 300ms) 1ms, visibility 1ms;
        visibility: visible;
      }

      .option {
        list-style: none;
      }

      .option.is-active {
        opacity: 1;
      }

      .link {
        color: inherit;
        display: flex;
        padding: 0.5rem 0;
        text-decoration: none;
        gap: var(--hh-spacing-xs);
      }

      .link-radio {
        display: block;
        min-width: 2.4rem;
        width: 2.4rem;
        height: 2.4rem;
        border: 1px solid var(--hh-color-brand-secondary);
        border-radius: 50%;
        position: relative;
      }

      .link-radio::before {
        content: '';
        width: calc(2.4rem / 2);
        height: calc(2.4rem / 2);
        border-radius: 50%;
        background-color: var(--hh-color-brand-secondary);
        scale: 0;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }

      .option.is-active .link-radio::before {
        scale: 1;
      }
    `,
  ]
}

if (!customElements.get('hh-custom-dropdown')) {
  customElements.define('hh-custom-dropdown', HHCustomDropdown);
}